import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ico01 from "@images/ico_01.svg";
import ico02 from "@images/ico_02.svg";
import ico03 from "@images/ico_03.svg";

function useIntersection(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, visible };
}

const CARD_ICONS = [
  { src: ico01, alt: "글로벌 문화 교류" },
  { src: ico02, alt: "지역 상생 경제" },
  { src: ico03, alt: "안전한 교류 환경" },
];
const CARD_KEYS = ["card1", "card2", "card3"] as const;

export default function SocialSection() {
  const { t } = useTranslation();
  const { ref, visible } = useIntersection();

  return (
    <section
      id="social"
      className="py-24 md:py-32 px-4"
      style={{ backgroundColor: "oklch(0.10 0.001 10)" }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div
          ref={ref}
          className={`text-center mb-14 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <p className="text-xs font-label tracking-widest uppercase mb-4" style={{ color: "oklch(0.74 0.18 55)" }}>
            {t("social.label")}
          </p>
          <h2 className="font-heading font-black text-3xl md:text-5xl mb-4 leading-tight" style={{ color: "white" }}>
            {t("social.title")}
          </h2>
          <p className="font-label text-sm md:text-base" style={{ color: "rgba(255,255,255,0.45)" }}>
            {t("social.desc")}
          </p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {CARD_KEYS.map((key, i) => (
            <div
              key={key}
              className={`rounded-xl p-8 border text-center transition-all duration-600 cursor-default
                ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
              style={{
                backgroundColor: "oklch(0.15 0.001 10)",
                borderColor: "rgba(255,255,255,0.06)",
                transitionDelay: `${i * 120}ms`,
              }}
            >
              <div
                className="w-16 h-16 flex items-center justify-center mx-auto mb-6 rounded-2xl"
                style={{ backgroundColor: "rgba(245,158,11,0.10)" }}
              >
                <img src={CARD_ICONS[i].src} alt={CARD_ICONS[i].alt} className="w-8 h-8" />
              </div>
              <h3 className="font-heading font-bold text-lg mb-3" style={{ color: "rgba(255,255,255,0.9)" }}>
                {t(`social.${key}_title`)}
              </h3>
              <p className="text-sm font-label leading-relaxed" style={{ color: "rgba(255,255,255,0.45)" }}>
                {t(`social.${key}_desc`)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}